// OS notifications, private by default (matches the desktop shell's policy): the body says
// only "Incoming call" unless the user opts in to caller details. Never transcript text.
import { APP_NAME } from '../config/brand.ts';

const SHOW_CALLER_KEY = 'sidetone.notify.show-caller';

export function showCallerInNotifications(): boolean {
  try {
    return window.localStorage.getItem(SHOW_CALLER_KEY) === '1';
  } catch {
    return false;
  }
}

export function setShowCallerInNotifications(show: boolean): void {
  try {
    if (show) window.localStorage.setItem(SHOW_CALLER_KEY, '1');
    else window.localStorage.removeItem(SHOW_CALLER_KEY);
  } catch {
    // Storage unavailable: the private default stays in effect.
  }
}

export function incomingCallNotice(caller: string | null, showCaller: boolean): { title: string; body: string } {
  return { title: APP_NAME, body: showCaller && caller ? `Incoming call from ${caller}` : 'Incoming call' };
}

type Notice = { close(): void; onclick: ((event: Event) => void) | null };
export interface NotificationEnvironment {
  background(): boolean;
  permitted(): boolean;
  showCaller(): boolean;
  create(title: string, options: NotificationOptions): Notice;
  focus(): void;
}
const browserEnvironment: NotificationEnvironment = {
  background: () => typeof document !== 'undefined' && (document.hidden || !document.hasFocus()),
  permitted: () => typeof Notification !== 'undefined' && Notification.permission === 'granted',
  showCaller: showCallerInNotifications,
  create: (title, options) => new Notification(title, options),
  focus: () => { window.focus(); document.getElementById('phone')?.focus(); },
};

/** Only the owner passes ringing calls here. Click opens the phone; Answer remains an explicit control. */
export class RingNotifications {
  private notices = new Map<string, Notice>();
  private shown = new Set<string>();
  constructor(private env: NotificationEnvironment = browserEnvironment) {}
  sync(ringing: readonly { id: string; caller: string | null }[], owner: boolean): void {
    const live = new Set(owner ? ringing.map(c => c.id) : []);
    for (const [id, notice] of this.notices) if (!live.has(id)) { notice.close(); this.notices.delete(id); }
    for (const id of this.shown) if (!live.has(id)) this.shown.delete(id);
    if (!owner || !this.env.background() || !this.env.permitted()) return;
    for (const call of ringing) {
      if (this.shown.has(call.id)) continue;
      const { title, body } = incomingCallNotice(call.caller, this.env.showCaller());
      try {
        const notice = this.env.create(title, { body: `${body}. Open the ringing phone to answer.`, tag: `sidetone-ring-${call.id}` });
        notice.onclick = () => {
          if (!this.notices.has(call.id)) return;
          this.env.focus();
          notice.close();
          this.notices.delete(call.id);
        };
        this.notices.set(call.id, notice);
        this.shown.add(call.id);
      } catch { /* Platform refused: the normal ringing phone remains available. */ }
    }
  }
  clear(): void { for (const notice of this.notices.values()) notice.close(); this.notices.clear(); this.shown.clear(); }
}
